/**
 * AI Help Page - Interactive AI Assistant for AskIsaac
 * Uses OpenAI GPT-5.2 via Emergent LLM Key for intelligent responses
 */

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Send,
  Bot,
  User,
  ArrowLeft,
  Sparkles,
  BookOpen,
  Loader2,
  RefreshCw,
  ChevronRight,
  Shield,
  Wrench,
  Package,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

// Quick action suggestions
const quickActions = [
  { text: "How do I submit a maintenance request?", icon: Wrench },
  { text: "What is QuickBid and how does it work?", icon: Shield },
  { text: "How do I track my backorders?", icon: Package },
  { text: "How do I register as a contractor?", icon: Building2 },
];

export default function AIHelpPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    // Add user message
    const userMessage = { role: "user", content: message };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/api/ai-assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        throw new Error("Failed to get response");
      }
    } catch (error) {
      console.error("AI chat error:", error);
      toast.error("Failed to get response. Please try again.");
      // Add error message
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I'm having trouble connecting right now. Please try again or contact support@sterilemate.com for assistance." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickAction = (text) => {
    sendMessage(text);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API}/api/ai-assistant/session/${sessionId}`, {
          method: "DELETE"
        });
      } catch (e) {
        // Ignore errors
      }
    }
    setMessages([]);
    setSessionId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={LOGO_URL} alt="AskIsaac" className="h-10 w-auto rounded-lg" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Isaac AI Assistant
                </h1>
                <p className="text-sm text-slate-400">Powered by GPT-5.2</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/help">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                  <BookOpen className="w-4 h-4 mr-2" />
                  User Guides
                </Button>
              </Link>
              {messages.length > 0 && (
                <Button 
                  variant="ghost" 
                  onClick={clearChat}
                  className="text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Chat Area */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 flex flex-col">
        {/* Welcome Screen - shown when no messages */}
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/25">
                <Bot className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Hi, I'm Isaac!</h2>
              <p className="text-lg text-slate-400 max-w-md mx-auto">
                Your AI-powered assistant for the AskIsaac healthcare platform. 
                Ask me anything about maintenance, backorders, QuickBid, or getting started.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="w-full max-w-2xl">
              <p className="text-sm text-slate-500 text-center mb-4">Try asking:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action.text)}
                    className="flex items-center gap-3 p-4 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-slate-600 rounded-xl text-left transition-all group"
                    data-testid={`quick-action-${index}`}
                  >
                    <div className="w-10 h-10 bg-slate-700 group-hover:bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                      <action.icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-slate-300 text-sm">{action.text}</span>
                    <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="flex-1 overflow-y-auto space-y-4 pb-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex items-start gap-3 max-w-[80%] ${
                    message.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      message.role === "user"
                        ? "bg-blue-600"
                        : "bg-gradient-to-br from-emerald-500 to-teal-600"
                    }`}
                  >
                    {message.role === "user" ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  {/* Message Bubble */}
                  <div
                    className={`px-4 py-3 rounded-2xl ${
                      message.role === "user"
                        ? "bg-blue-600 text-white rounded-tr-none"
                        : "bg-slate-800 text-slate-100 rounded-tl-none border border-slate-700/50"
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl rounded-tl-none bg-slate-800 border border-slate-700/50">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Isaac is thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input Area */}
        <div className="mt-4">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask Isaac anything about AskIsaac..."
                className="w-full bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-12 py-6 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                disabled={isLoading}
                data-testid="ai-chat-input"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-6 rounded-xl disabled:opacity-50"
              data-testid="ai-chat-send"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>
          <p className="text-xs text-slate-500 text-center mt-3">
            Isaac can make mistakes. For critical issues, contact{" "}
            <a href="mailto:support@sterilemate.com" className="text-emerald-400 hover:underline">
              support@sterilemate.com
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
